import { Table, TableColumnsType } from "antd";
import _ from "lodash";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getAttendanceSummary } from "../../../api/statistics.api";
import useAxios from "../../../hooks/useAxios";
import config from "../../../lib/config";
import exportTable from "../../../lib/exportTable";
import { IAttendanceSummary, IGetAttendanceSummary } from "../../../models/statistics.model";
import { RootState } from "../../../store/rootReducer";
import DownloadButton from "../../DownloadButton";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import Pagination from "../../Pagination";

interface IProps {
  rawData: IAttendanceSummary[]|undefined;
  total: number|undefined;
  isAllCampus: boolean;
  pageNo: number;
  setPageNo: (pageNo: number) => void;
  srtDate: string;
  endDate: string;
  campusCodes: string[];
}

const Summary: FC<IProps> = ({
  rawData,
  total,
  isAllCampus,
  pageNo,
  setPageNo,
  srtDate,
  endDate,
  campusCodes,
}) => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetAttendanceSummary>();

  const [data, setData] = useState<IAttendanceSummary[]>([]);

  useEffect(() => {
    if (rawData) {
      setData(handleData(rawData));
    }
  } ,[rawData]);

  useEffect(() => {
    if (res?.data && !isLoading) {
      const _data: IAttendanceSummary[] = _.cloneDeep(res.data);
      _data.forEach(item => {
        item.attendRate = Math.round(item.attendRate);
        item.studentCount = (item.unidentifiedCount + item.attendCount + item.absentCount + item.earlyLeaveCount + item.lateCount + item.recognitionAbsenceCount);
      });
      const currentDate = moment(new Date());
      exportTable(columns, handleData(_data), `${t('statistics.attendance.title1')}(${t('statistics.attendance.Tab1')})_(${currentDate.format('YYYYMMDD')})`, `${t('statistics.attendance.Tab1')}`, [12, 12, 10, 10, 10, 10, 12]);
    }
  }, [res, isLoading]);

  const handleData = (data: IAttendanceSummary[]) => {
    const _data: IAttendanceSummary[] = _.cloneDeep(data);
    if (data.length && isAllCampus) {
      _data.unshift(calculateTotal(data));
    }

    return _data;
  }

  const calculateTotal = (rawData: IAttendanceSummary[]) => {
    const total: IAttendanceSummary = {
      campusCode: '',
      campusKorName: t('statistics.attendance.3'),
      campusEngName: 'Total',
      attendCount: 0,
      absentCount: 0,
      recognitionAbsenceCount: 0,
      lateCount: 0,
      earlyLeaveCount: 0,
      attendRate: 0,
      unidentifiedCount: 0,
    }

    rawData.forEach(item => {
      total.attendCount += item.attendCount;
      total.absentCount += item.absentCount;
      total.recognitionAbsenceCount += item.recognitionAbsenceCount;
      total.lateCount += item.lateCount;
      total.earlyLeaveCount += item.earlyLeaveCount;
      total.unidentifiedCount += item.unidentifiedCount;
    });

    total.attendRate = Math.round(total.attendCount / (total.attendCount + total.absentCount + total.recognitionAbsenceCount) * 100);

    return total;
  };

  const onExportTable = () => {
    if (srtDate && endDate && campusCodes) {
      execute(getAttendanceSummary({
        srtDate,
        endDate,
        campusCode: auth.hqYn === 'Y'
          ? campusCodes
          : (campusCodes.length ? campusCodes : auth.campus),
        pageNo: 1,
        perPage: 999999999,
      }));
    }
  }

  const columns: TableColumnsType<IAttendanceSummary> = [
    {
      title: t('class.class.placeholder1'),
      dataIndex: 'campusKorName',
      key: 'campusKorName',
      align: 'center',
    },
    {
      title: t('statistics.attendance.column12'),
      dataIndex: 'studentCount',
      key: 'studentCount',
      align: 'center',
      render: (value, record) => {return (record.unidentifiedCount + record.attendCount + record.absentCount + record.earlyLeaveCount + record.lateCount + record.recognitionAbsenceCount)}
    },
    {
      title: t('statistics.attendance.column11'),
      dataIndex: 'unidentifiedCount',
      key: 'unidentifiedCount',
      align: 'center',
    },
    {
      title: t('statistics.attendance.column1'),
      dataIndex: 'attendCount',
      key: 'attendCount',
      align: 'center',
    },
    {
      title: t('statistics.attendance.column2'),
      dataIndex: 'absentCount',
      key: 'absentCount',
      align: 'center',
    },
    {
      title: t('statistics.attendance.column3'),
      dataIndex: 'recognitionAbsenceCount',
      key: 'recognitionAbsenceCount',
      align: 'center',
    },
    {
      title: t('statistics.attendance.column4'),
      dataIndex: 'lateCount',
      key: 'lateCount',
      align: 'center',
    },
    {
      title: t('statistics.attendance.column5'),
      dataIndex: 'earlyLeaveCount',
      key: 'earlyLeaveCount',
      align: 'center',
    },
    {
      title: t('statistics.attendance.column6'),
      dataIndex: 'attendRate',
      key: 'attendRate',
      align: 'center',
      render: value => Math.round(value),
    },
  ];

  return (
    <>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />

      <div className="comm-table__area">
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            current: pageNo,
            pageSize: config.pageSize,
          }}
          rowClassName={(record: IAttendanceSummary) => record.campusCode === '' ? 'tfoot' : ''}
        />
      </div>
      <div className="comm-bx__between">
        <DownloadButton onClick={onExportTable} />
        <div className="common-paging center">
          <Pagination
            count={total || 0}
            page={pageNo}
            setPage={setPageNo}
          />
        </div>
      </div>
    </>
  );
}

export default Summary;
